import type { ConnectorRestrictionResponse, UpsertConnectorRestrictionRequest } from '../types';
import type { RequestFn } from '../../client';

/**
 * Admin-only allowlist for phased connector rollouts.
 *
 * Default-open semantics: a connector with no restriction row is
 * usable by every merchant (current behavior). A connector with a
 * row is gated — only merchants in `allowed_merchant_ids` can attach
 * a connector account. Enforced server-side at MCA-create time.
 */
export class ConnectorRestrictions {
  constructor(private readonly request: RequestFn) {}

  /**
   * Idempotent upsert. If a row exists for `connector_name`, its
   * allowlist + reason are replaced. To open the connector to
   * everyone again, call `delete()`.
   */
  async upsert(body: UpsertConnectorRestrictionRequest): Promise<ConnectorRestrictionResponse> {
    return this.request('POST', '/admin/connector-restrictions', { body });
  }

  async list(): Promise<ConnectorRestrictionResponse[]> {
    return this.request('GET', '/admin/connector-restrictions');
  }

  async retrieve(connectorName: string): Promise<ConnectorRestrictionResponse> {
    return this.request(
      'GET',
      `/admin/connector-restrictions/${encodeURIComponent(connectorName)}`,
    );
  }

  /** Removing the row makes the connector public again. */
  async delete(connectorName: string): Promise<{ connector_name: string; deleted: boolean }> {
    return this.request(
      'DELETE',
      `/admin/connector-restrictions/${encodeURIComponent(connectorName)}`,
    );
  }
}
